/* ==========================================================================
   home-combo-showcase.js
   Behavior for sections/home-combo-showcase.liquid:
     - "View Pack of N" popover toggle (one open at a time, closes on
       outside click / Escape)
     - Scroll-triggered pop-in reveal for the card grid
     - Click-and-drag horizontal scrolling for mouse users (touch already
       scrolls natively via overflow-x)
     - Prev/Next carousel arrows that page the row by one card width

   Real Add to Cart (Product mode) is handled by the theme's native
   <product-form-component> — this file only owns the decorative bits that
   are unique to this section's markup.
   ========================================================================== */

(function () {
  'use strict';

  var GRID_ATTR = 'data-combo-grid';
  var INIT_ATTR = 'data-sj-hcs-init';

  function closeAllPacks(grid) {
    var open = grid.querySelectorAll('.sj-hcs-card.is-pack-open');
    open.forEach(function (card) {
      card.classList.remove('is-pack-open');
      var toggle = card.querySelector('.sj-hcs-viewpack');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  }

  var INTERACTIVE_SELECTOR = 'button, a, input, select, textarea';

  function initDragScroll(grid, dragState) {
    var isPointerDown = false;
    var startX = 0;
    var startScrollLeft = 0;

    grid.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      // Never start a drag from a button/link/toggle — let it always click
      // normally. Dragging still works from anywhere else on the card
      // (image, text, padding).
      if (e.target.closest(INTERACTIVE_SELECTOR)) return;
      isPointerDown = true;
      dragState.dragged = false;
      startX = e.clientX;
      startScrollLeft = grid.scrollLeft;
      // Pointer capture is requested later, once an actual drag is
      // confirmed in pointermove below — not here. Capturing eagerly on
      // every pointerdown also retargets the resulting click event's
      // target to `grid` itself (even for a plain, non-dragging click),
      // which silently broke "click a card to open its product page"
      // since the click handler could never find the actual card.
    });

    grid.addEventListener('pointermove', function (e) {
      if (!isPointerDown) return;
      var delta = e.clientX - startX;
      // A real click still has a few pixels of incidental mouse movement
      // between mousedown and mouseup — 4px was tight enough that a normal
      // click on a card kept getting misclassified as a drag (which then
      // swallows the click, including the "open product page" navigation
      // below), so the threshold for what counts as an intentional drag is
      // a bit more forgiving here.
      if (!dragState.dragged && Math.abs(delta) > 10) {
        dragState.dragged = true;
        grid.classList.add('is-dragging');
        if (grid.setPointerCapture) {
          try {
            grid.setPointerCapture(e.pointerId);
          } catch (err) {
            // ignore — not fatal if capture isn't supported for this pointer
          }
        }
      }
      if (dragState.dragged) {
        grid.scrollLeft = startScrollLeft - delta;
      }
    });

    function stopDrag() {
      isPointerDown = false;
      grid.classList.remove('is-dragging');
    }

    grid.addEventListener('pointerup', stopDrag);
    grid.addEventListener('pointerleave', stopDrag);
    grid.addEventListener('pointercancel', stopDrag);
  }

  function initCarouselArrows(grid) {
    var carousel = grid.closest('.sj-hcs-carousel');
    if (!carousel) return;
    var prev = carousel.querySelector('[data-combo-prev]');
    var next = carousel.querySelector('[data-combo-next]');
    if (!prev || !next) return;

    function updateArrowState() {
      var maxScroll = grid.scrollWidth - grid.clientWidth;
      prev.disabled = grid.scrollLeft <= 1;
      next.disabled = maxScroll <= 1 || grid.scrollLeft >= maxScroll - 1;
    }

    function scrollByCard(direction) {
      var card = grid.querySelector('.sj-hcs-card');
      if (!card) return;
      var gap = parseFloat(getComputedStyle(grid).gap) || 0;
      var amount = (card.getBoundingClientRect().width + gap) * direction;
      grid.scrollBy({ left: amount, behavior: 'smooth' });
    }

    prev.addEventListener('click', function () {
      scrollByCard(-1);
    });
    next.addEventListener('click', function () {
      scrollByCard(1);
    });

    var ticking = false;
    grid.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        updateArrowState();
        ticking = false;
      });
    });
    window.addEventListener('resize', updateArrowState);

    updateArrowState();
  }

  function initGrid(grid) {
    if (grid.hasAttribute(INIT_ATTR)) return;
    grid.setAttribute(INIT_ATTR, '1');

    var dragState = { dragged: false };
    initDragScroll(grid, dragState);
    initCarouselArrows(grid);

    // Capture phase: swallow the click that follows a drag gesture before
    // it can toggle a pack popover or activate a card's button/link.
    grid.addEventListener(
      'click',
      function (e) {
        if (dragState.dragged) {
          e.preventDefault();
          e.stopPropagation();
          dragState.dragged = false;
        }
      },
      true
    );

    // "View Pack of N" opens on hover (mouse) / focus (keyboard) of just
    // that toggle text/button — not the whole card. A short close delay
    // lets the pointer travel from the toggle down into the dropdown
    // (a sibling element, not a descendant of the toggle) without it
    // closing along the way.
    grid.querySelectorAll('.sj-hcs-card').forEach(function (card) {
      var toggle = card.querySelector('.sj-hcs-viewpack');
      if (!toggle) return;
      var dropdown = card.querySelector('.sj-hcs-pack-dropdown');
      var closeTimer = null;

      function openPack() {
        if (closeTimer) {
          clearTimeout(closeTimer);
          closeTimer = null;
        }
        closeAllPacks(grid);
        card.classList.add('is-pack-open');
        toggle.setAttribute('aria-expanded', 'true');
      }

      function closePackNow() {
        if (closeTimer) {
          clearTimeout(closeTimer);
          closeTimer = null;
        }
        card.classList.remove('is-pack-open');
        toggle.setAttribute('aria-expanded', 'false');
      }

      function scheduleClosePack() {
        if (closeTimer) clearTimeout(closeTimer);
        closeTimer = setTimeout(closePackNow, 120);
      }

      // Touch devices have no hover, so tapping needs its own explicit
      // open/close toggle — it can't just lean on the 'focus' listener
      // below, since tapping an already-focused button fires no new
      // 'focus' event (only the very first tap does). Capturing the
      // open/closed state on pointerdown, before that tap's own
      // focus/click side effects run, lets the click handler tell
      // "already open from a previous tap" apart from "not open yet" and
      // toggle accordingly, every time.
      var wasOpenBeforeThisPress = false;
      toggle.addEventListener('pointerdown', function () {
        wasOpenBeforeThisPress = card.classList.contains('is-pack-open');
      });

      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        if (wasOpenBeforeThisPress) {
          closePackNow();
        } else {
          openPack();
        }
      });

      toggle.addEventListener('mouseenter', openPack);
      toggle.addEventListener('mouseleave', scheduleClosePack);
      toggle.addEventListener('focus', openPack);
      toggle.addEventListener('blur', scheduleClosePack);

      if (dropdown) {
        dropdown.addEventListener('mouseenter', function () {
          if (closeTimer) {
            clearTimeout(closeTimer);
            closeTimer = null;
          }
        });
        dropdown.addEventListener('mouseleave', scheduleClosePack);
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAllPacks(grid);
    });

    // Clicking anywhere else on a card (image, title, padding) opens that
    // card's product page. Clicks on any interactive element (the pack
    // toggle, Add to Cart, its quantity input, or a manual button link)
    // or inside the open pack dropdown are left alone.
    grid.addEventListener('click', function (e) {
      if (e.target.closest(INTERACTIVE_SELECTOR)) return;
      if (e.target.closest('.sj-hcs-pack-dropdown')) return;
      var card = e.target.closest('.sj-hcs-card');
      if (!card) return;
      var url = card.getAttribute('data-card-url');
      if (url) window.location.href = url;
    });

    var cards = grid.querySelectorAll('.sj-hcs-card');
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion || !('IntersectionObserver' in window)) {
      cards.forEach(function (card) {
        card.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -40px 0px' }
    );
    cards.forEach(function (card) {
      observer.observe(card);
    });
  }

  function init() {
    document.querySelectorAll('[' + GRID_ATTR + ']').forEach(initGrid);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify Theme Editor live-reload support
  document.addEventListener('shopify:section:load', init);
})();
